import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';

interface PriceRowFull {
  service: string;
  href?: string;
  price: string;
  inclus: string;
}

const PARTICULIER_ROWS: PriceRowFull[] = [
  { service: 'Dératisation (rats, souris)', href: '/deratisation-guadeloupe', price: '75 € – 260 €', inclus: 'Diagnostic, postes sécurisés, traitement, passage de contrôle, conseils prévention' },
  { service: 'Dératisation infestation lourde', href: '/deratisation-guadeloupe', price: '200 € – 400 €', inclus: 'Plusieurs zones, passages renforcés' },
  { service: 'Ravets / cafards', href: '/traitement-ravets-cafards-guadeloupe', price: '100 € – 250 €', inclus: 'Gel professionnel, points d\'entrée, garantie de résultat' },
  { service: 'Ravets infestation lourde (blatte germanique)', href: '/traitement-ravets-cafards-guadeloupe', price: '250 € – 410 €', inclus: '2 passages inclus' },
  { service: 'Scolopendres (barrière périmétrique)', href: '/scolopendres-guadeloupe', price: '120 € – 280 €', inclus: 'Traitement refuges + ceinture périmétrique' },
  { service: 'Punaises de lit (protocole complet)', href: '/punaises-de-lit-guadeloupe', price: '90 € – 350 €', inclus: '2-3 passages inclus, vapeur + insecticide ciblé' },
  { service: 'Moustiques (jardin)', href: '/demoustication-guadeloupe', price: '120 € – 300 €', inclus: 'Audit gîtes, larvicide, zones de repos' },
  { service: 'Nid de guêpes', href: '/desinsectisation-guadeloupe', price: '90 € – 180 €', inclus: 'Destruction + retrait si accessible' },
  { service: 'Termites — curatif maison', href: '/traitement-termites-guadeloupe', price: '1 000 € – 3 000 €', inclus: 'Après inspection ; pièges-appâts ou barrière, garantie pluriannuelle' },
  { service: 'Termites — bois localisé (meuble, poutre)', href: '/traitement-termites-guadeloupe', price: 'Dès 150 €', inclus: 'Injection / pulvérisation' },
  { service: 'Fourmis, mites, puces, mouches', href: '/desinsectisation-guadeloupe', price: '90 € – 200 €', inclus: 'Traitement ciblé' },
];

const PRO_ROWS: PriceRowFull[] = [
  { service: 'Contrat annuel restaurant / snack (rongeurs + insectes, registre HACCP)', price: 'Dès 40-50 €/mois', inclus: 'Passages planifiés, registre, urgences, interlocuteur direct' },
  { service: 'Contrat hôtel / gîte (plan complet + urgences prioritaires)', price: 'Sur devis', inclus: 'Traitement toutes zones, interventions programmées hors présence clients' },
  { service: 'Syndics / bailleurs (parties communes)', price: 'Sur devis', inclus: 'Caves, locaux poubelles, gaines techniques' },
  { service: 'Intervention ponctuelle urgente pro', price: 'Sur devis', inclus: 'Intervention prioritaire, diagnostic + traitement en un passage' },
];

const FAQ_ITEMS = [
  {
    question: 'Combien coûte une dératisation en Guadeloupe ?',
    answer: 'Entre 75 € et 260 € TTC pour un logement standard, jusqu\'à 400 € en infestation lourde. Diagnostic, traitement, contrôle et conseils inclus.',
  },
  {
    question: 'Combien coûte un dératiseur pour un restaurant ?',
    answer: 'En contrat annuel, à partir de 40-50 €/mois avec registre HACCP et urgences incluses — moins cher qu\'une seule fermeture administrative.',
  },
  {
    question: 'Le devis est-il vraiment gratuit ?',
    answer: 'Oui, et sans engagement : photo WhatsApp → prix en 2 h. La visite de diagnostic est gratuite quand elle débouche sur une intervention.',
  },
  {
    question: 'Y a-t-il des frais de déplacement ?',
    answer: 'Nos fourchettes s\'entendent déplacement inclus partout en Guadeloupe.',
  },
  {
    question: 'Proposez-vous des facilités de paiement ?',
    answer: 'Pour les traitements termites (1 000-3 000 €), un paiement en plusieurs fois peut être mis en place — parlez-en au technicien lors du devis.',
  },
];

function PriceTableFull({ rows, compact = false }: { rows: PriceRowFull[]; compact?: boolean }) {
  const cellPadding = compact ? 'px-4 py-3' : 'px-4 md:px-6 py-3 md:py-4';
  const textBase = compact ? 'text-sm' : 'text-sm md:text-base';
  const textSm = compact ? 'text-xs' : 'text-xs md:text-sm';

  return (
    <div className="w-full overflow-hidden rounded-xl border border-background-300/60">
      <table className="w-full text-left">
        <thead>
          <tr className="bg-primary-500">
            <th className={`${cellPadding} text-white font-heading font-bold ${textBase}`}>Intervention</th>
            <th className={`${cellPadding} text-white font-heading font-bold ${textBase} text-right whitespace-nowrap`}>Fourchette TTC</th>
            <th className={`${cellPadding} text-white font-heading font-bold ${textBase} hidden md:table-cell`}>Inclus</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const content = (
              <tr
                className={`${index % 2 === 0 ? 'bg-background-100' : 'bg-white'} transition-colors hover:bg-background-200/70`}
              >
                <td className={`${cellPadding} text-foreground-800 ${textBase}`}>
                  {row.href ? (
                    <span className="text-secondary-500 font-medium hover:text-secondary-600 cursor-pointer">{row.service}</span>
                  ) : (
                    row.service
                  )}
                </td>
                <td className={`${cellPadding} text-foreground-950 font-bold ${textBase} text-right whitespace-nowrap`}>
                  {row.price}
                </td>
                <td className={`${cellPadding} text-foreground-600 ${textSm} hidden md:table-cell`}>
                  {row.inclus}
                </td>
              </tr>
            );
            if (row.href) {
              return (
                <Link key={row.service + index} to={row.href} className="contents cursor-pointer">
                  {content}
                </Link>
              );
            }
            return <Fragment key={row.service + index}>{content}</Fragment>;
          })}
        </tbody>
      </table>
    </div>
  );
}

import { Fragment } from 'react';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

export default function Tarifs() {
  useDocumentHead({
    title: 'Tarifs dératisation & désinsectisation en Guadeloupe',
    description: 'Combien coûte une dératisation ou un traitement ravets en Guadeloupe ? Tous nos tarifs affichés, prix ferme annoncé avant intervention. Devis en 2 h.',
    jsonLd: [faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Tarifs', path: '/tarifs' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Tarifs'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tarifs-1.webp"
            alt="Technicien Gwada Nuisibles présentant une grille tarifaire transparente devant une propriété en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Nos tarifs : des prix clairs, annoncés avant l&rsquo;intervention
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Rare dans le métier, systématique chez nous : on vous annonce un prix ferme AVANT d&rsquo;intervenir, et il ne bouge pas. Les fourchettes ci-dessous correspondent aux cas réels rencontrés en Guadeloupe ; votre prix exact dépend de la surface, du niveau d&rsquo;infestation et de l&rsquo;accessibilité — il est fixé gratuitement en 2 h sur photo WhatsApp ou après visite.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* TARIFS PARTICULIERS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Tarifs particuliers
              </h2>
            </div>
            <div className="max-w-4xl mx-auto">
              <PriceTableFull rows={PARTICULIER_ROWS} />
              <p className="text-foreground-500 text-sm mt-5 text-center leading-relaxed">
                Traitements groupés (ex. ravets + scolopendres, rats + ravets) : remise sur devis — les nuisibles partagent souvent les mêmes accès, autant tout traiter d&rsquo;un coup.
              </p>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS PROFESSIONNELS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Tarifs professionnels
              </h2>
            </div>
            <div className="max-w-4xl mx-auto">
              <PriceTableFull rows={PRO_ROWS} />
              <p className="text-foreground-500 text-sm mt-5 text-center leading-relaxed">
                Le contrat professionnel inclut : passages planifiés, registre et plan d&rsquo;appâtage conformes HACCP, intervention d&rsquo;urgence entre deux passages, interlocuteur direct.
              </p>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CE QUI FAIT VARIER LE PRIX */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Ce qui fait varier le prix (en toute transparence)
              </h2>
              <ul className="space-y-3 text-foreground-600 text-base leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>La surface et le nombre de pièces ou de bâtiments</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Le niveau d&rsquo;infestation (préventif &lt; début d&rsquo;infestation &lt; colonie installée)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>L&rsquo;accessibilité (galetas, vide sanitaire, toiture)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Le nombre de passages nécessaires (punaises et blatte germanique en demandent toujours plusieurs — ils sont inclus dans nos forfaits, pas facturés en supplément surprise)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>L&rsquo;urgence (intervention jour même)</span>
                </li>
              </ul>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* POURQUOI ON AFFICHE NOS PRIX */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Pourquoi on affiche nos prix (et pas les autres)
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed">
                Parce qu&rsquo;un client qui connaît la fourchette avant d&rsquo;appeler est un client qui ne se sent pas piégé. Le devis reste gratuit, le prix annoncé est ferme, et s&rsquo;il faut un passage de plus que prévu dans un forfait, c&rsquo;est pour nous.
              </p>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur nos tarifs
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Votre prix exact en 2 h, sur photo."
      />

      <ContactFormBlock source="tarifs" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}